// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { WithRequiredNotNull } from '@evolvemep/api-ts';
import { Button, Divider, Flex, Stack, Text, TextInput } from '@mantine/core';

import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { StepperInput } from 'components/Mantine/StepperInput';
import { BendPreviewContextProvider } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { BendDimensionInputs } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendDimensionInputs';
import { BendMachineInfoInputs } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendMachineInfoInputs';
import {
  type BendCreatorFormValues,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { BendCreatorShell } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/BendCreatorShell';
import { useBendCreatorPreviewData } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/useBendCreatorPreviewData';

export type ValidatedFormData = WithRequiredNotNull<
  BendCreatorFormValues,
  'benderProfile' | 'bendType' | 'conduitTypeId' | 'conduitSizeId' | 'partName' | 'quantity' | 'materialStockLength'
>;

type BendCreatorFormProps = {
  onClose: () => void;
  isEditing?: boolean;
  onSubmit: (v: ValidatedFormData) => void;
};

type BendCreatorFormShellProps = {
  isEditing?: boolean;
  onClose: () => void;
  navPanel: ReactNode;
};

/** Hosts the form in the fullscreen shell. Split out so the preview hooks run inside `BendPreviewContextProvider`. */
const BendCreatorFormShell = ({ isEditing, onClose, navPanel }: BendCreatorFormShellProps) => {
  const {
    values: { bendType },
  } = useBendCreatorFormContext();
  const bendPreviewData = useBendCreatorPreviewData();
  return (
    <BendCreatorShell
      bendType={bendType}
      bendPreviewData={bendPreviewData}
      navPanel={navPanel}
      // EditBendModal keeps its own header with a close button until edit mode moves onto the shell.
      onClose={isEditing ? undefined : onClose}
      bendsToDiscard={0}
    />
  );
};

export const BendCreatorForm = ({ onClose, isEditing, onSubmit: parentOnSubmit }: BendCreatorFormProps) => {
  const form = useBendCreatorFormContext();

  const { lastValidation, isValidating } = useBendCreatorValidationContext();
  const serverSideValid = lastValidation?.isSuccess && !isValidating;

  const handleSubmit = async (values: typeof form.values) => {
    if (!serverSideValid) return;
    // Validation ensures that all required fields will be non-null
    const validatedValues = values as ValidatedFormData;
    parentOnSubmit(validatedValues);
  };

  return (
    <BendPreviewContextProvider>
      <BendCreatorFormShell
        isEditing={isEditing}
        onClose={onClose}
        navPanel={
          <form
            noValidate
            onSubmit={form.onSubmit(handleSubmit)}
            style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <Stack p="md" style={{ overflowY: 'auto' }}>
              <Flex justify="space-between">
                <Text size="xl" fw={500}>
                  Define Bend
                </Text>
                <Button variant="subtle" type="reset" onClick={form.reset}>
                  Reset form
                </Button>
              </Flex>
              <BendMachineInfoInputs />
              <Divider />
              <BendDimensionInputs />
              <Divider />
              <Stack gap={4}>
                <TextInput label="Part Name" disabled={isEditing} required {...form.getInputProps('partName')} />
                <StepperInput
                  mode="count"
                  label="Quantity"
                  disabled={isEditing}
                  required
                  min={1}
                  max={99}
                  step={1}
                  suffix=" ea"
                  {...form.getInputProps('quantity')}
                />
                {!isEditing && (
                  <Text c="dimmed" fz="sm">
                    Part name will be incremented when adding quantities over 1.
                  </Text>
                )}
              </Stack>
            </Stack>
            <ModalButtons
              type="submit"
              onClose={onClose}
              confirmationText={isValidating ? 'Validating bend data' : isEditing ? 'Update bend' : 'Add to work order'}
              disabled={!form.isValid() || !serverSideValid}
              loading={isValidating}
            />
          </form>
        }
      />
    </BendPreviewContextProvider>
  );
};
